import { faTimes } from "@fortawesome/pro-light-svg-icons";
import { Icon } from "common/components/core/icon";
import PropTypes from "prop-types";
import { cloneElement, isValidElement, useRef } from "react";
import ReactDOM from "react-dom";
import styled, { css, keyframes, createGlobalStyle } from "styled-components";

import { log } from "common/components/debug";
import { usePortalEl, useNoScroll, useScrollY, useKeyboardHandler } from "common/hooks";

/**
 * @param {ModalProps} modalProps
 * @returns JSX.Element
 */
export function Modal({
    actions,
    children,
    className,
    close,
    closeOnEsc,
    closeOnOverlayClick,
    fullScreen,
    header, // do not pass in header and headerChildren, do one or the other, or neither
    headerChildren, // TODO determine if refactoring this component is worth it to keep the list of passed-in props from growing
    modal,
    noPadding,
    overlay,
    portalId = "modal-default-portal",
    root,
    show,
    showCloseIcon,
    $zIndex
}) {
    /* state hooks */
    const modalContainerRef = useRef();
    const modalRootRef = useRef(root || document.getElementById("wrapper") || document.body);

    /* effects */
    const portalEl = usePortalEl(modalRootRef, portalId);
    useKeyboardHandler("Escape", () => closeOnEsc && close({
        className, root, portalId, header
    }));
    useNoScroll(show && overlay && modal);
    useScrollY(show && overlay && modal, modalContainerRef);

    /* render */
    const content = (
        <ModalContainer ref={modalContainerRef} overlay={overlay} className={className}>
            <PortalGlobalStyle portalId={portalId} />
            {overlay && <ModalOverlay $zIndex={$zIndex} onClick={() => closeOnOverlayClick && close()} />}
            <ModalWindow fullScreen={fullScreen} noPadding={noPadding} $zIndex={$zIndex} className="modalWindow">
                {header && !headerChildren && (
                    <ModalHeader className="header">
                        <div className="title">{header}</div>
                        { showCloseIcon && <Icon $shouldHover icon={faTimes} fontSize={1} onClick={close}/> }
                    </ModalHeader>
                )}
                {headerChildren && !header && (
                    <ModalHeader className="header">
                        { showCloseIcon && <Icon $shouldHover icon={faTimes} fontSize={1} onClick={close}/> }
                        {headerChildren()}
                    </ModalHeader>
                )}
                <ModalBody className="body">
                    {children && cloneElement(children, { ...children.props, close })}
                </ModalBody>
                {Array.isArray(actions) && actions.length > 0 && (
                    <ModalFooter className="modal-buttons">
                        {actions.map((item) => {
                            if (isValidElement(item)) return item;
                            return (
                                <span key={item.text}>
                                    <button type="button" className={`btn ${item.classname}`} onClick={item.handler}>{item.text}</button>
                                </span>
                            );
                        })}
                    </ModalFooter>
                )}
            </ModalWindow>
        </ModalContainer>
    );

    return show ? ReactDOM.createPortal(content, portalEl.current) : null;
}

Modal.defaultProps = {
    show: false,
    overlay: true,
    noPadding: false,
    fullScreen: false,
    modal: true,
    showCloseIcon: true,
    close: (...args) => log("Close method Not implemented", args),
    closeOnOverlayClick: true,
    closeOnEsc: true,
    className: "",
    actions: []
};

Modal.propTypes = {
    portalId: PropTypes.string.isRequired,
    show: PropTypes.bool,
    overlay: PropTypes.bool,
    noPadding: PropTypes.bool,
    fullScreen: PropTypes.bool,
    showCloseIcon: PropTypes.bool,
    close: PropTypes.func,
    closeOnOverlayClick: PropTypes.bool,
    closeOnEsc: PropTypes.bool,
    modal: PropTypes.bool,
    className: PropTypes.string,
    actions: PropTypes.arrayOf(PropTypes.exact({
        text: PropTypes.oneOf([PropTypes.element, PropTypes.string]).isRequired,
        classname: PropTypes.string,
        handler: PropTypes.func.isRequired
    }))
};

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 0.5; }
`;

// const slideIn = keyframes`
//     from { transform: translateY(-1500px); }
//     to { transform: translateY(0); }
// `;

const scaleIn = keyframes`
    from { transform: scale(0); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
`;

const PortalGlobalStyle = createGlobalStyle`
    #${(props) => props.portalId} {
        margin: inherit;
        padding: inherit;
    }
`;

const ModalContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: inherit;
    padding: inherit;
    
    ${({ overlay }) => overlay && css`
        width: 100%;
        height: 100%;
    `}
`;

const ModalOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors["paz-black"]};
    opacity: 0.5;
    z-index: ${({ $zIndex }) => $zIndex || 1010};
    animation: .7s ${fadeIn};
`;

const ModalWindow = styled.div`
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => theme.colors["canvas-light"]};
    padding: 15px;
    z-index: ${({ $zIndex }) => $zIndex || 1011};
    animation: .1s ease-out ${scaleIn};
    max-height: 95%;
    
    ${({ noPadding }) => noPadding && css`
        padding: 0;
    `}
    
    ${({ fullScreen }) => fullScreen && css`
        height: 100%;
        width: 100%;
        z-index: ${({ $zIndex }) => $zIndex || 1030};
        animation: none;
        max-height: none;
    `}
`;

const ModalHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-bottom: 10px;
    
    .title {
        display: flex;
        flex: 1 0 auto;
        justify-content: flex-start;
        align-items: center;
        font-family: ${({ theme }) => theme.text.medium};
        font-size: ${({ theme }) => theme.headerFontSize};
    }
`;

const ModalBody = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    margin-bottom: 10px;
    overflow: auto;
`;

const ModalFooter = styled.div`
    display: flex;
    flex-direction: row;
    flex: 0 0 auto;
    justify-content: flex-end;
    align-items: center;
    
    span {
        margin: 0 5px;
    }
`;
